import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Form } from "react-bootstrap";
import styled from "styled-components";
import { colors } from "utils";

const INITIAL_VISIBLE = 50;
const VISIBLE_CHUNK = 50;

const Wrapper = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.nightlyWoods200 || "#E5E7EB"};
  border-radius: 8px;
  overflow: hidden;
`;

const Toolbar = styled.div`
  align-items: center;
  border-bottom: 1px solid ${colors.nightlyWoods200 || "#E5E7EB"};
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 12px 16px;
`;

const Scroll = styled.div`
  max-width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  border-collapse: collapse;
  font-size: 13px;
  width: 100%;
`;

const Th = styled.th`
  background: ${(p) => p.$bgColor || colors.milkGlass200 || "#F3F4F6"};
  border-bottom: 1px solid ${colors.nightlyWoods200 || "#E5E7EB"};
  cursor: ${(p) => (p.$sortable ? "pointer" : "default")};
  padding: 10px 12px;
  text-align: left;
  user-select: none;
  white-space: nowrap;
`;

const Td = styled.td`
  background: ${(p) => p.$bgColor || "transparent"};
  border-bottom: 1px solid ${colors.nightlyWoods100 || "#F3F4F6"};
  padding: 8px 12px;
  vertical-align: top;
  white-space: nowrap;
`;

const Empty = styled.div`
  color: ${colors.nightlyWoods700 || "#6B7280"};
  padding: 32px;
  text-align: center;
`;

const Sentinel = styled.div`
  align-items: center;
  border-top: 1px solid ${colors.nightlyWoods200 || "#E5E7EB"};
  color: ${colors.nightlyWoods700 || "#6B7280"};
  display: flex;
  font-size: 12px;
  justify-content: center;
  padding: 12px;
`;

const sortIndicator = (col) => {
  const dir = col.getIsSorted();
  if (dir === "asc") return " ▲";
  if (dir === "desc") return " ▼";
  return "";
};

export const DataTable = ({
  data,
  columns,
  emptyMessage = "No rows.",
  toolbar = null,
}) => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const sentinelRef = useRef(null);

  const memoData = useMemo(() => data || [], [data]);
  const memoColumns = useMemo(() => columns, [columns]);

  const table = useReactTable({
    data: memoData,
    columns: memoColumns,
    state: { globalFilter, sorting },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const allRows = table.getRowModel().rows;
  const totalRows = allRows.length;
  const visibleRows = allRows.slice(0, visibleCount);
  const hasMore = visibleCount < totalRows;

  // Reset visible window when the underlying data array changes (re-run, rollup change).
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [data]);

  // Grow visibleCount as the sentinel scrolls into view.
  useEffect(() => {
    if (!hasMore) return undefined;
    const target = sentinelRef.current;
    if (!target) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((c) => c + VISIBLE_CHUNK);
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, visibleCount]);

  return (
    <Wrapper>
      <Toolbar>
        <Form.Control
          type="search"
          placeholder="Filter rows..."
          value={globalFilter ?? ""}
          onChange={(e) => setGlobalFilter(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <div style={{ alignItems: "center", display: "flex", gap: 12 }}>
          <span style={{ color: colors.nightlyWoods700 || "#6B7280", fontSize: 12 }}>
            {hasMore
              ? `Showing ${visibleRows.length.toLocaleString()} of ${totalRows.toLocaleString()} rows`
              : `${totalRows.toLocaleString()} row${totalRows === 1 ? "" : "s"}`}
          </span>
          {toolbar}
        </div>
      </Toolbar>
      <Scroll>
        {totalRows === 0 ? (
          <Empty>{emptyMessage}</Empty>
        ) : (
          <Table>
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => {
                    const sortable = header.column.getCanSort();
                    const bgColor = header.column.columnDef.meta?.bgColor;
                    return (
                      <Th
                        key={header.id}
                        $sortable={sortable}
                        $bgColor={bgColor}
                        onClick={
                          sortable
                            ? header.column.getToggleSortingHandler()
                            : undefined
                        }
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {sortable && sortIndicator(header.column)}
                      </Th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      key={cell.id}
                      $bgColor={cell.column.columnDef.meta?.bgColor}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Scroll>
      {hasMore && <Sentinel ref={sentinelRef}>Loading more rows...</Sentinel>}
    </Wrapper>
  );
};
